/* @flow */
import QueryParams from "./QueryParams";
import { getLanguageSet } from "nx-components/Locales";

class UserAgent {
  value: string;
  _passiveSupported: boolean;
  constructor() {
    this.value = window.navigator.userAgent.toLowerCase();
  }

  hasString(key: string) {
    return this.value.indexOf(key) !== -1;
  }

  isIOS() {
    return this.isIPhone() || this.isIPod() || this.isIPad();
  }

  isIPhone() {
    return this.hasString("iphone");
  }

  isIPod() {
    return this.hasString("ipod");
  }

  isIPad() {
    return this.hasString("ipad");
  }

  isAndroid() {
    return this.hasString("android");
  }

  isCrossWalk() {
    return this.hasString("crosswalk");
  }

  getChromeVersion() {
    const chromeVersionMatches = this.value.match(/chrome\/([\d.]+)/);
    return chromeVersionMatches ? chromeVersionMatches[1] : null;
  }

  isAndroidPhone() {
    return this.isAndroid() && this.hasString("mobile");
  }

  isAndroidTablet() {
    return this.isAndroid() && !this.hasString("mobile");
  }

  isBlackberry() {
    return (
      this.hasString("blackberry") ||
      this.hasString("bb10") ||
      this.hasString("rim")
    );
  }

  isBlackberryPhone() {
    return this.isBlackberry() && !this.hasString("tablet");
  }

  isBlackberryTablet() {
    return this.isBlackberry() && this.hasString("tablet");
  }

  isWindows() {
    return this.hasString("windows");
  }

  isWindowsPhone() {
    return this.isWindows() && this.hasString("phone");
  }

  isWindowsPhone81() {
    return this.isWindowsPhone() && this.hasString("8.1");
  }

  isWindowsPhone10() {
    return this.isWindowsPhone() && this.hasString("10.0");
  }

  isWindows10WebView() {
    return (
      this.hasString("webview") && this.isWindows() && this.hasString("10.0")
    );
  }

  isWindowsTablet() {
    return this.isWindows() && this.hasString("touch");
  }

  isInternetExplorer() {
    return this.hasString("msie") || this.hasString("trident/");
  }

  isEdge() {
    return this.hasString("edge");
  }

  isPhone() {
    return (
      this.isAndroidPhone() ||
      this.isIPhone() ||
      this.isIPod() ||
      this.isWindowsPhone() ||
      this.isBlackberryPhone()
    );
  }

  isTablet() {
    return (
      this.isIPad() ||
      this.isAndroidTablet() ||
      this.isBlackberryTablet() ||
      this.isWindowsTablet()
    );
  }

  webViewDescription() {
    const localeSet = getLanguageSet();
    const language = localeSet.nexia_mobile.UserAgent;
    let webViewDescription = "";
    if (this.isAndroid()) {
      webViewDescription = `${language.chromeVersion}: `;
      webViewDescription += this.getChromeVersion() || language.unknownVersion;
      if (this.isCrossWalk()) {
        const nbsp = String.fromCharCode(160);
        webViewDescription += nbsp;
        webViewDescription += "(Crosswalk)";
      }
    } else if (this.isIOS()) {
      const platformParam = QueryParams.platform();
      if (platformParam === "ios-uiwebview") {
        webViewDescription = `${language.webView}: UIWebView`;
      } else if (platformParam === "ios-wkwebview") {
        webViewDescription = `${language.webView}: WKWebView`;
      }
    }

    return webViewDescription;
  }

  hasPassiveEventListeners() {
    if (this._passiveSupported != null) {
      return this._passiveSupported;
    }

    // Test for the "passive" options for event listeners (iOS 10+, etc)
    // https://developer.mozilla.org/en-US/docs/Web/API/EventTarget/addEventListener
    this._passiveSupported = false;

    try {
      // $FlowIgnore: defineProperty is poorly typed in core.
      let options = Object.defineProperty({}, "passive", {
        get() {
          this._passiveSupported = true;
        },
      });

      window.addEventListener("test", options, options);
      window.removeEventListener("test", options, options);
    } catch (err) {
      this._passiveSupported = false;
    }
    return this._passiveSupported;
  }
}

export default new UserAgent();



// WEBPACK FOOTER //
// ../nx-components/src/UserAgent.js